import React, { useMemo, useState } from 'react';
import { CardType, ConditionalCard } from '../carousels/components/conditionalCard/ConditionalCard';

import { Skeleton } from 'components/skeleton';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { RootState } from 'lib/centralStore';
import { useSelector } from 'react-redux';
import { Category } from 'types/categoryEnum';
import { isTruthy } from 'utility/functions';
import { pageSize } from '../../utility/constant';
import { CardTypes } from '../cards/types';
import { Wrapper } from '../wrapper';
import styles from './CardList.module.scss';
import { CardListEmptyResult } from './components/cardListEmptyResult';

export type CardListProps<T extends CardTypes> = {
  typeOfItem: CardType;
  children?: React.ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
  filteredItems: T[];
  totalCount?: number | undefined;
  totalPages?: number | undefined;
  hasNoFilters?: boolean;
  handleClickLoadMore?: Function;
};

export function CardList<T>({
  typeOfItem,
  children,
  isLoading,
  isFetching,
  filteredItems,
  totalCount,
  totalPages,
  hasNoFilters,
  handleClickLoadMore,
}: CardListProps<T>) {
  const viewMoreLabel = useGetLabelByKey('view-more')!;
  const [slotPerView, setSlotPerView] = useState<number>(pageSize);

  const { allItems, pageNumber } = useSelector((store: RootState) => store.archiveSlot);

  const isLoadMoreVisible = useMemo(() => {
    const wTotalPages = totalPages ?? 0;
    return wTotalPages > 1 && pageNumber < wTotalPages && filteredItems?.length > 0 && allItems.length >= pageSize;
  }, [allItems.length, filteredItems?.length, pageNumber, totalPages]);

  const handleLoadMore = () => {
    setSlotPerView(slotPerView + pageSize);
  };

  const renderCards = () => {
    let lstToRender = Array.isArray(filteredItems) ? filteredItems : [];

    if (!handleClickLoadMore) {
      lstToRender = lstToRender?.slice(0, slotPerView);
    }

    if (isTruthy(lstToRender?.length)) {
      return (
        <React.Fragment key={'giochi-di-carte'}>
          {lstToRender.map((x: CardTypes) => (
            <ConditionalCard key={`${x?.slug}_${x?.id}`} type={typeOfItem} item={x} containerClass={styles.card} />
          ))}
        </React.Fragment>
      );
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <React.Fragment key={'giochi-di-carte'} />;
  };

  const { showChild, showSkeleton, showEmptyResult } = useMemo(() => {
    const showChild = children && totalCount !== 0 && !isLoading;
    const showSkeleton = (isFetching && !isLoading) || (isLoading && !isTruthy(filteredItems?.length));
    const showEmptyResult = !isFetching && !isLoading && !isTruthy(filteredItems?.length) && totalCount === 0;

    return { showChild, showSkeleton, showEmptyResult };
  }, [children, filteredItems?.length, isFetching, isLoading, totalCount]);

  return (
    <Wrapper
      noPaddingTop={typeOfItem === Category.Casino || filteredItems?.length === 0 || hasNoFilters ? false : true}
    >
      <div className={styles.container}>
        <div className={styles.list}>
          {renderCards()}
          {showChild && <article className={styles.childrenContainer}>{children}</article>}
        </div>
        {showSkeleton && <Skeleton />}
        {isLoadMoreVisible && (
          <button
            type="button"
            className={styles.loadMore}
            onClick={() => (handleClickLoadMore ? handleClickLoadMore() : handleLoadMore())}
          >
            {viewMoreLabel}
          </button>
        )}
        {showEmptyResult && <CardListEmptyResult type={typeOfItem} />}
      </div>
    </Wrapper>
  );
}
